// What recording a vet visit does to the rest of the app. Pure, so it can be
// tested; ids are derived from the visit, so editing a visit updates these
// records rather than adding duplicates.
//
//   weighed at the vet      → a weight entry at the visit time
//   given / prescribed      → a medication entry each; a course → "last dose" reminder
//   follow-up booked        → a care-plan item (and so a calendar entry)
//   worth claiming          → an admin reminder to claim, a week later
// Care items ticked as "done at this visit" are completed by the caller
// (completion reschedules repeats, which needs the live store).

import { addDays, formatDate, toISODate } from './time.ts';
import type { CareItem, LogEvent, Profile, VetVisit } from './types.ts';

/** A medicine row's key: its own id (index only for visits saved before rows had ids). */
export const medKey = (m: { id?: string }, i: number) => m.id ?? String(i);

export interface VisitEffects {
  events: LogEvent[];
  care: CareItem[];
  /** Derived record ids this visit owns (to remove ones no longer wanted). */
  eventIds: string[];
  careIds: string[];
}

export function visitEffects(v: VetVisit, profile?: Pick<Profile, 'insurer'>): VisitEffects {
  const day = toISODate(v.at);
  const where = v.practice ?? 'the vet';
  const events: LogEvent[] = [];
  const care: CareItem[] = [];
  if (v.weightKg && v.weightKg > 0)
    events.push({ id: `visit-${v.id}-weight`, type: 'weight', at: v.at, by: v.by, source: 'app', data: { kg: v.weightKg }, note: `Weighed at ${where}` });
  (v.meds ?? []).forEach((m, i) => {
    if (!m.name.trim()) return;
    const k = medKey(m, i);
    events.push({ id: `visit-${v.id}-med-${k}`, type: 'meds', at: v.at, by: v.by, source: 'app', data: { name: m.name.trim(), dose: m.dose?.trim() || undefined }, note: [`From ${where}`, m.instructions?.trim()].filter(Boolean).join(' — ') });
    if (m.days && m.days > 1)
      care.push({ id: `visit-${v.id}-course-${k}`, kind: 'other', title: `Last dose of ${m.name.trim()}`, due: addDays(day, m.days - 1), why: m.instructions?.trim() || 'Finish the whole course, even if he seems better.', edited: true });
  });
  if (v.followUp?.date)
    care.push({ id: `visit-${v.id}-follow`, kind: 'vet', title: v.followUp.what?.trim() || `Follow-up at ${where}`, due: v.followUp.date, why: v.reason ? `Follow-up to: ${v.reason}` : undefined, edited: true });
  if (v.claim && v.cost)
    care.push({ id: `visit-${v.id}-claim`, kind: 'admin', title: `Claim £${v.cost.toFixed(2).replace(/\.00$/, '')} from ${profile?.insurer || 'the insurer'}`, due: addDays(day, 7), why: 'Ask the vet for an itemised invoice and the claim form signed. Most insurers want claims within a few months.', edited: true });
  return { events, care, eventIds: events.map((e) => e.id), careIds: care.map((c) => c.id) };
}

/** Every id a visit could own (now or before an edit), so stale ones can be removed. */
export function possibleVisitIds(v: VetVisit, previous?: VetVisit): { events: string[]; care: string[] } {
  const keys = [...new Set([...(v.meds ?? []).map(medKey), ...(previous?.meds ?? []).map(medKey)])];
  return {
    events: [`visit-${v.id}-weight`, ...keys.map((k) => `visit-${v.id}-med-${k}`)],
    care: [`visit-${v.id}-follow`, `visit-${v.id}-claim`, ...keys.map((k) => `visit-${v.id}-course-${k}`)],
  };
}

/** The parts of a derived record that come from the visit (to tell whether an edit changed them). */
export function derivedSignature(r: LogEvent | CareItem): string {
  return 'type' in r ? JSON.stringify([r.type, r.at, r.data, r.note]) : JSON.stringify([r.kind, r.title, r.due, r.why]);
}

/** One line for lists, the vet report and Ask. */
export function describeVisit(v: VetVisit, care: CareItem[] = []): string {
  const when = new Date(v.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const done = (v.careDone ?? []).map((id) => care.find((c) => c.id === id)?.title).filter(Boolean);
  return [
    `${when} ${v.kind === 'call' ? 'phone call with' : 'visit to'} ${v.practice ?? 'the vet'}${v.vet ? ` (${v.vet})` : ''}${v.reason ? ` — ${v.reason}` : ''}.`,
    v.weightKg ? `Weighed ${v.weightKg} kg.` : '',
    done.length ? `Done: ${done.join(', ')}.` : '',
    v.notes?.trim() ? `Vet said: ${v.notes.trim()}` : '',
    v.meds?.length ? `Given: ${v.meds.map((m) => [m.name, m.dose, m.instructions].filter(Boolean).join(' ')).join('; ')}.` : '',
    v.answers?.length ? v.answers.map((a) => `Q: ${a.q} A: ${a.a}`).join(' ') : '',
    v.followUp?.date ? `Follow-up ${formatDate(v.followUp.date)}${v.followUp.what ? `: ${v.followUp.what}` : ''}.` : '',
  ]
    .filter(Boolean)
    .join(' ');
}
